/*
 * Entry point of the application.
 * Only platform bootstrapping code should be here.
 * For app-specific initialization, use `app/app.component.ts`.
 */

import {bootstrapApplication} from "@angular/platform-browser";
import {datadogLogs} from "@datadog/browser-logs";
import {datadogRum} from "@datadog/browser-rum";

import {appConfig} from "@app/app.config";
import {AppComponent} from "@app/app.component";
import {registerSwiperElements} from "@app/shared/swiper-elements";
import {environment} from "@env/environment";

const CHUNK_RELOAD_KEY = "hc:chunk-reload-attempted";

function isChunkLoadError(error: unknown): boolean {
  const message =
    typeof error === "string"
      ? error
      : error instanceof Error
        ? error.message
        : typeof error === "object" && error !== null && "message" in error
          ? String((error as {message?: unknown}).message ?? "")
          : "";

  return [
    "Loading chunk",
    "ChunkLoadError",
    "Failed to fetch dynamically imported module",
    "Importing a module script failed",
  ].some((pattern) => message.includes(pattern));
}

function hardReloadAfterChunkFailure(): void {
  if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === "1") {
    return;
  }

  sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
  window.location.reload();
}

function installChunkReloadRecovery(): void {
  window.addEventListener("error", (event: ErrorEvent) => {
    if (isChunkLoadError(event.error ?? event.message)) {
      hardReloadAfterChunkFailure();
    }
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) {
      hardReloadAfterChunkFailure();
    }
  });
}

registerSwiperElements();
installChunkReloadRecovery();
logBuildVersion();

function logBuildVersion(): void {
  fetch("/version.json?_t=" + Date.now())
    .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
    .then((info: {version: string; imageTag?: string; gitShortSha?: string; buildDate?: string}) => {
      const tag = info.imageTag ?? `v${info.version}`;
      const sha = info.gitShortSha ? ` (${info.gitShortSha})` : "";
      const built = info.buildDate ? ` — built ${info.buildDate}` : "";
      // eslint-disable-next-line no-console
      console.log(
        `%c Hypecast WebApp ${tag}${sha}${built} `,
        "background:#0029FF;color:#fff;border-radius:3px;padding:2px 6px",
      );
    })
    .catch(() => {
      // version.json not available (local dev without generate:version) — silently ignore
    });
}

function initRum(env: string): void {
  const applicationId = environment.datadogRumApplicationId;
  if (!applicationId || applicationId === "REPLACE_WITH_RUM_APP_ID") {
    return;
  }
  datadogRum.init({
    applicationId,
    clientToken: "pub6fc1ba4ef1956e7f1b935eb41f4226ce",
    site: "datadoghq.eu",
    service: "webapp",
    env,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: "mask-user-input",
  });
}

if (environment.production) {
  datadogLogs.init({
    clientToken: "puba04ca3df02f8a665c25814136dbf4d42",
    site: "datadoghq.eu",
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    service: "webapp",
    env: "hypecast.one",
  });
  initRum("hypecast.one");
}

if (environment.environmentName === "QA") {
  datadogLogs.init({
    clientToken: "puba04ca3df02f8a665c25814136dbf4d42",
    site: "datadoghq.eu",
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    service: "webapp",
    env: "hypecast.dev",
  });
  initRum("hypecast.dev");
}

bootstrapApplication(AppComponent, appConfig)
  .then(() => {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  })
  .catch((err) => console.error(err));
